/**
 * VisionSelectionContext — "the user has just chosen their main image".
 *
 * ── The bug this exists to end ─────────────────────────────────────────────
 *
 * AnchorRevealGate is a full-screen overlay mounted in AppLayout, which makes
 * it a SIBLING of the routed page rather than a child of it. Confirming a
 * selection makes the overlay disappear, and what that uncovers is the page
 * that was sitting underneath the whole time. It never unmounted, never
 * remounted, and nothing told it anything had happened, so it comes back
 * exactly as it was: Home still offering "make my image" for an image the user
 * has just picked and named. A manual page refresh was the only way forward.
 *
 * Nothing errored, because nothing failed. Nothing was listening. That is the
 * second time this exact shape has bitten (operator, 2026-08-16, and the
 * reveal-never-appearing bug the same morning), which is why the signal is now
 * a named thing rather than another one-off.
 *
 * ── Why a context ──────────────────────────────────────────────────────────
 *
 * There is no `dispatchEvent` anywhere in this app and there are two existing
 * contexts, so a context is what the surrounding code looks like. A window
 * CustomEvent would also be untyped and invisible to a component test.
 *
 * ── Why a nonce and not a boolean ──────────────────────────────────────────
 *
 * Selection happens more than once. A regenerated set stands beside the
 * original rather than replacing it (D-17), so a user can confirm, regenerate
 * and confirm again inside one session. A boolean that has already flipped to
 * true fires no effect the second time, which would fix today's report and
 * leave the same dead end one step further along.
 *
 * ── Scope, deliberately one fact ───────────────────────────────────────────
 *
 * This is NOT a general app event bus, and it should not grow into one.
 * `/api/home` has exactly two writers: a generation landing or failing, which
 * already arrives over Reverb, and a confirmed selection, which is this. A bus
 * would invite every future component to publish into it and would make the
 * question "what invalidates Home" unanswerable again.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface VisionSelectionValue {
  /**
   * Increments once per confirmed selection. `0` means "none so far this
   * session", so an effect that re-reads on change must skip that first value
   * or it duplicates the consumer's own mount fetch.
   */
  selectionNonce: number;
  notifyVisionSelected: () => void;
}

/**
 * A working no-op default rather than a throwing one. Consumers are ordinary
 * pages, and a page rendered in a test without the provider should render, not
 * explode on a signal it does not need.
 */
const VisionSelectionContext = createContext<VisionSelectionValue>({
  selectionNonce: 0,
  notifyVisionSelected: () => {},
});

export function VisionSelectionProvider({ children }: { children: ReactNode }) {
  const [selectionNonce, setSelectionNonce] = useState(0);

  const notifyVisionSelected = useCallback(() => {
    setSelectionNonce((n) => n + 1);
  }, []);

  const value = useMemo(
    () => ({ selectionNonce, notifyVisionSelected }),
    [selectionNonce, notifyVisionSelected],
  );

  return (
    <VisionSelectionContext.Provider value={value}>{children}</VisionSelectionContext.Provider>
  );
}

export function useVisionSelection(): VisionSelectionValue {
  return useContext(VisionSelectionContext);
}
